import HeroSection from "./components/HeroSection";
import QuickEligibilitySection from "./components/QuickEligibilitySection";
import HowFastComparisonSection from "./components/HowFastComparisonSection";
import WhyFasterSection from "./components/WhyFasterSection";
import ProcessingSpeedsSection from "./components/ProcessingSpeedsSection";
import HowItWorksSection from "./components/HowItWorksSection";
import WhoThisIsForSection from "./components/WhoThisIsForSection";
import FAQSection from "./components/FAQSection";
import FinalCTASection from "./components/FinalCTASection";
import RelatedPassportServices from "@/components/feature/RelatedPassportServices";

const pageUrl = "https://fastpassportcenter.com/passport-services/fast-passport-renewal/";

const faqSchemaItems = [
  {
    "@type": "Question",
    name: "How fast can I renew my passport?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "With Fast Passport Center, you can renew your passport in as little as 3 business days from when we receive your documents. Add 1–2 days for your documents to reach us if mailing, plus overnight FedEx return delivery. Most customers in our 3-day tier have their renewed passport in hand within 5–7 days of starting their order.",
    },
  },
  {
    "@type": "Question",
    name: "Can I renew my passport without going to a passport office?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes — if you qualify for renewal using Form DS-82, the entire process is done by mail. You never need to visit a passport agency, an acceptance facility, or make any appointments. You mail us your documents and we handle the rest. This is the biggest advantage of qualifying for renewal over a new passport application.",
    },
  },
  {
    "@type": "Question",
    name: "My passport expired — can I still renew it fast?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes, as long as your passport was issued within the last 15 years and meets the other DS-82 eligibility requirements. An expired passport does not disqualify you from renewal by mail. We renew expired passports every day — it's one of our most common requests.",
    },
  },
  {
    "@type": "Question",
    name: "How do I know if I qualify for fast renewal or need a new passport?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "You qualify for fast renewal (DS-82) if: your passport was issued when you were 16 or older, issued within the last 15 years, is undamaged, and is in your current legal name (or you can document a name change). If any of these don't apply, you'll need a new passport application (DS-11) instead. Call 877-253-0084 if you're not sure — we'll tell you in 2 minutes.",
    },
  },
  {
    "@type": "Question",
    name: "What documents do I need for fast passport renewal?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "For renewal by mail (DS-82): your current passport, Form DS-82 (completed and signed), one 2x2 inch passport photo taken within the last 6 months, proof of travel (flight itinerary or travel confirmation), and any name change documentation if applicable (marriage certificate, divorce decree, or court order). Fast Passport Center provides a personalized checklist when you start your order.",
    },
  },
  {
    "@type": "Question",
    name: "How is Fast Passport Center faster than going through the post office?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "The post office puts your application in a standard mail queue — government expedited processing through the mail takes 2–3 weeks, plus mailing time on both ends. Fast Passport Center is a registered State Dept. courier with reserved daily submission slots at passport agencies. We hand-deliver your application the next business day, bypassing the mail queue entirely. That's why we can process renewals in 3–9 business days instead of weeks.",
    },
  },
  {
    "@type": "Question",
    name: "Why is Fast Passport Center cheaper than FedEx or Staples?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "FedEx Office and Staples are resellers — they charge a service fee and then send your application to a registered courier like us. You pay their markup plus the actual courier cost. When you use Fast Passport Center directly, you pay only our direct courier fee — no middleman, no retail markup. We are typically 20–80% less expensive than retail passport service counters.",
    },
  },
  {
    "@type": "Question",
    name: "Can I renew my passport fast if I recently got married and changed my name?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes — if your passport was issued within the last 15 years and meets the other DS-82 requirements, you can renew by mail even with a name change. You'll need to include your certified marriage certificate with your renewal application. Note: if your passport was issued less than 1 year ago and your only change is the name, you may qualify for Form DS-5504 instead — a free name-change correction form. Call us and we'll tell you which applies to your situation.",
    },
  },
  {
    "@type": "Question",
    name: "What if I need to renew my passport but my trip is in less than 3 days?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Call us immediately at 877-253-0084. A sub-3-day window is extremely tight. Whether it's possible depends on what day it is, what type of renewal you need, and how quickly your documents can reach us. We'll give you an honest assessment immediately and tell you exactly what's possible.",
    },
  },
  {
    "@type": "Question",
    name: "Will I get my old passport back after fast renewal?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes. Your old, cancelled passport is returned to you separately after your renewal is processed — it typically arrives within a few weeks of your new passport. Keep it in a safe place as it may contain valid visas you'll want to use for future travel.",
    },
  },
];

const serviceSchema = {
  "@type": "Service",
  name: "Fast Passport Renewal",
  provider: {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "1-877-253-0084",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
    },
  },
  description: "Fast passport renewal service from a registered US Department of State courier. Renewal in as little as 3 business days. No office visit required for eligible renewals.",
  areaServed: "US",
  offers: {
    "@type": "Offer",
    price: "195",
    priceCurrency: "USD",
    description: "Fast passport renewal starting from $195 for 9 business day processing, up to $345 for 3 business day processing",
  },
};

const breadcrumbSchema = {
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com" },
    { "@type": "ListItem", position: 2, name: "Passport Services", item: "https://fastpassportcenter.com/passport-services/" },
    { "@type": "ListItem", position: 3, name: "Fast Passport Renewal", item: pageUrl },
  ],
};

const graph = [
  {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "+1-877-253-0084",
    email: "contactus@fastpassportcenter.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
      addressCountry: "US",
    },
    areaServed: { "@type": "Country", name: "United States" },
    priceRange: "$$$",
    openingHours: ["Mo-Fr 09:00-18:00"],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
  },
  serviceSchema,
  breadcrumbSchema,
  { "@type": "FAQPage", mainEntity: faqSchemaItems },
];

const jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

function FastPassportRenewalSEOHead() {
  return (
    <>
      <title>Fast Passport Renewal — Renew in as Little as 3 Business Days | Fast Passport Center</title>
      <meta name="description" content="Renew your U.S. passport fast with Fast Passport Center — a registered State Dept courier. Renewal in as little as 3 business days. No office visit required. 20–80% less than FedEx or Staples. A+ BBB, 20+ years." />
      <meta name="keywords" content="fast passport renewal, renew passport fast, how fast can I renew my passport, passport renewal fast, expedited passport renewal, renew passport in 3 days, fastest way to renew passport, passport renewal same week" />
      <meta name="last-modified" content="2026-07-01" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Fast Passport Renewal | Renew in 3 Business Days" />
      <meta property="og:description" content="Renew your passport in as little as 3 business days. Registered State Dept courier. No office visit. A+ BBB rated. 20+ years experience." />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Fast Passport Center" />
      <meta property="og:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Fast Passport Renewal | Renew in 3 Business Days | Fast Passport Center" />
      <meta name="twitter:description" content="Renew your passport in as little as 3 business days. Registered State Dept courier. No office visit. A+ BBB rated. 20+ years experience." />
      <meta name="twitter:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <link rel="canonical" href={pageUrl} />
      <script type="application/ld+json" id="structured-data-fast-passport-renewal" dangerouslySetInnerHTML={{ __html: jsonLd }} />
    </>
  );
}

export default function FastPassportRenewalPage() {
  return (
    <div className="font-sans">
      <FastPassportRenewalSEOHead />
      <HeroSection />
      <QuickEligibilitySection />
      <HowFastComparisonSection />
      <WhyFasterSection />
      <ProcessingSpeedsSection />
      <HowItWorksSection />
      <WhoThisIsForSection />
      <FAQSection />

      <RelatedPassportServices
        services={[
          {
            title: "Urgent Passport Service",
            description: "Trip coming up sooner than you thought? Urgent processing in as little as 3 business days.",
            link: "/passport-services/urgent-passport/",
            icon: "ri-timer-line",
          },
          {
            title: "Expedited Passport Service",
            description: "Compare all speed tiers side-by-side to find the right fit for your travel date.",
            link: "/passport-services/expedited-passport-service/",
            icon: "ri-speed-up-line",
          },
          {
            title: "Passport Expediting Service",
            description: "Learn how our registered State Dept courier system gets results faster than retail counters.",
            link: "/passport-services/passport-expediting-service/",
            icon: "ri-government-line",
          },
          {
            title: "Emergency Passport",
            description: "Traveling in less than 72 hours? Same-day and next-day emergency processing available.",
            link: "/passport-services/emergency-passport/",
            icon: "ri-alert-line",
          },
        ]}
        colorClass="text-[#1B4D3E]"
        bgClass="bg-[#F0F7F4]"
        borderClass="border-[#D4E8DF]"
        linkColorClass="text-[#1B4D3E]"
      />

      <FinalCTASection />

      {/* Footer */}
      <footer className="bg-[#1B4D3E] text-green-100 py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Fast Passport Center</h4>
              <p className="text-green-200/80 text-sm leading-relaxed mb-3">
                Registered U.S. Department of State passport courier. A+ BBB rated. 20+ years of passport and visa experience.
              </p>
              <p className="text-green-200/60 text-xs">
                1629 K Street, Suite 300<br />Washington, DC 20006
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-green-200/80">
                <li><a href="/passport-services/fast-passport-renewal/" className="hover:text-white transition-colors cursor-pointer">Fast Passport Renewal</a></li>
                <li><a href="/passport-services/expedited-passport-service/" className="hover:text-white transition-colors cursor-pointer">Expedited Passport Service</a></li>
                <li><a href="/passport-services/emergency-passport/" className="hover:text-white transition-colors cursor-pointer">Emergency Passport</a></li>
                <li><a href="/passport-services/new-passport/" className="hover:text-white transition-colors cursor-pointer">New Passport</a></li>
                <li><a href="/passport-services/renewal/" className="hover:text-white transition-colors cursor-pointer">Passport Renewal (DS-82)</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-green-200/80">
                <li><a href="tel:8772530084" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">877-253-0084</a></li>
                <li><a href="mailto:contactus@fastpassportcenter.com" className="hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                <li><a href="/helpful-resources/" className="hover:text-white transition-colors cursor-pointer">Helpful Resources</a></li>
                <li><a href="/helpful-resources/passport-processing-times/" className="hover:text-white transition-colors cursor-pointer">Processing Times</a></li>
                <li><a href="/helpful-resources/passport-fees/" className="hover:text-white transition-colors cursor-pointer">Passport Fees</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Trust &amp; Credentials</h4>
              <ul className="space-y-2 text-sm text-green-200/80">
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line"></i> A+ BBB Accredited
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-government-fill"></i> Registered State Dept. Courier
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-history-line"></i> 20+ Years Experience
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-lock-line"></i> Secure &amp; Confidential
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-green-700/50 pt-8 text-center text-xs text-green-200/50">
            <p>&copy; {new Date().getFullYear()} Fast Passport Center. All rights reserved. Fast Passport Center is a private passport courier service registered with the U.S. Department of State. We are not a government agency.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}